import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Save, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../UI/table';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Badge } from '../UI/badge';
import { ModalDialog } from '../UI/modal-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../UI/pagination';
import { EmptyState } from '../UI/empty-state';
import { TableShimmer } from '../UI/table-shimmer';
import { useLocale } from '../../contexts/LocaleContext';
import { TENANT_NODE_COLUMNS, getEmptyRowForNodeKey } from '../../config/tenantNodeColumns';
import { themeClasses } from '../../config/themeClasses';

const PAGE_SIZE = 25;

function rowsEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function TenantNodeDataTable({
  nodeKey,
  rows = [],
  loading = false,
  saving = false,
  onSaveRows,
  onDirtyChange,
}) {
  const { t } = useLocale();
  const columns = TENANT_NODE_COLUMNS[nodeKey] || [];
  const [draftRows, setDraftRows] = useState([]);
  const [page, setPage] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [deleteIndex, setDeleteIndex] = useState(null);

  React.useEffect(() => {
    setDraftRows(Array.isArray(rows) ? rows.map((r) => ({ ...r })) : []);
    setPage(1);
  }, [rows, nodeKey]);

  const isDirty = useMemo(() => !rowsEqual(draftRows, rows), [draftRows, rows]);

  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const totalPages = Math.max(1, Math.ceil(draftRows.length / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return draftRows.slice(start, start + PAGE_SIZE);
  }, [draftRows, page]);

  const openAdd = () => {
    setEditIndex(null);
    setEditRow(getEmptyRowForNodeKey(nodeKey));
    setEditOpen(true);
  };

  const openEdit = (globalIndex) => {
    setEditIndex(globalIndex);
    setEditRow({ ...draftRows[globalIndex] });
    setEditOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIndex == null) return;
    setDraftRows((prev) => prev.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  const handleEditSave = () => {
    if (editIndex == null) {
      setDraftRows((prev) => [...prev, { ...editRow }]);
    } else {
      setDraftRows((prev) =>
        prev.map((row, i) => (i === editIndex ? { ...editRow } : row))
      );
    }
    setEditOpen(false);
    setEditIndex(null);
    setEditRow({});
  };

  const handleSaveAll = () => {
    onSaveRows?.(nodeKey, draftRows);
  };

  const globalIndexForPageRow = (pageRowIndex) => (page - 1) * PAGE_SIZE + pageRowIndex;

  if (loading && draftRows.length === 0) {
    return <TableShimmer rows={6} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
          <span>{t('tenantNodeRowsLoaded', { count: draftRows.length })}</span>
          {isDirty ? (
            <Badge variant="secondary">{t('tenantUnsavedChanges')}</Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            {t('tenantTableAddRow')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSaveAll}
            disabled={saving || loading || !isDirty}
          >
            <Save className="h-4 w-4" />
            {saving ? t('tenantSavingRows') : t('tenantSaveRows')}
          </Button>
        </div>
      </div>

      {draftRows.length === 0 && !loading ? (
        <EmptyState
          title={t('tenantTableEmptyTitle')}
          description={t('tenantTableEmptyDescription')}
          actionLabel={t('tenantTableAddRow')}
          onAction={openAdd}
        />
      ) : (
        <>
          <div
            className={`${themeClasses.appKit.sectionShell} overflow-hidden ${nodeKey === 'supplierFacilities' ? 'overflow-x-auto' : ''}`}
          >
            <Table className={nodeKey === 'supplierFacilities' ? 'min-w-max' : undefined}>
              <TableHeader>
                <TableRow>
                  {columns.map((col) => (
                    <TableHead key={col.key}>{t(col.labelKey)}</TableHead>
                  ))}
                  <TableHead className="w-[100px]">{t('tenantTableActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row, pageIndex) => {
                  const globalIndex = globalIndexForPageRow(pageIndex);
                  return (
                    <TableRow key={`${nodeKey}-row-${globalIndex}`}>
                      {columns.map((col) => (
                        <TableCell key={col.key} className="max-w-[200px] truncate">
                          {row[col.key] ?? '—'}
                        </TableCell>
                      ))}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openEdit(globalIndex)}
                            aria-label={t('tenantTableEditRow')}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setDeleteIndex(globalIndex)}
                            aria-label={t('tenantTableDeleteRow')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 ? (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const showEllipsis = prev != null && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {showEllipsis ? (
                          <PaginationItem>
                            <span className="px-2 text-text-tertiary">…</span>
                          </PaginationItem>
                        ) : null}
                        <PaginationItem>
                          <PaginationLink isActive={p === page} onClick={() => setPage(p)}>
                            {p}
                          </PaginationLink>
                        </PaginationItem>
                      </React.Fragment>
                    );
                  })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          ) : null}
        </>
      )}

      <ModalDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={editIndex == null ? t('tenantTableAddRow') : t('tenantTableEditRow')}
        className={nodeKey === 'supplierFacilities' ? 'max-w-5xl' : undefined}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
              {t('cancel')}
            </Button>
            <Button type="button" onClick={handleEditSave}>
              {t('tenantTableSaveRow')}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {columns.map((col) => (
            <div key={col.key} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-text-secondary">{t(col.labelKey)}</label>
              <Input
                value={editRow[col.key] ?? ''}
                onChange={(e) =>
                  setEditRow((prev) => ({ ...prev, [col.key]: e.target.value }))
                }
              />
            </div>
          ))}
        </div>
      </ModalDialog>

      <ModalDialog
        open={deleteIndex != null}
        onClose={() => setDeleteIndex(null)}
        title={t('tenantDeleteRowTitle')}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setDeleteIndex(null)}>
              {t('cancel')}
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete}>
              {t('tenantDeleteRowConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('tenantDeleteRowDescription')}</p>
      </ModalDialog>
    </div>
  );
}

export default TenantNodeDataTable;
